/**
 * OfferIntelTab — property workspace tab for BP-028.
 *
 * Compact multi-strategy offer matrix inline on the property workspace.
 * Renders the recommended offer + a mini comparison of the 5 strategies.
 * Deep-links to `/v3/intel/offer-intel` for the full negotiation console.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Coins, KeyRound, Handshake, RefreshCcw, Layers, Loader2,
  Sparkles, Award, ArrowUpRight, DollarSign, Timer, Percent,
} from "lucide-react";

const STRATEGY_META = {
  cash:            { label: "Cash",           icon: Coins,     accent: "#F59E0B" },
  subject_to:      { label: "Subject-To",     icon: KeyRound,  accent: "#8B5CF6" },
  seller_finance:  { label: "Seller Finance", icon: Handshake, accent: "#0EA5E9" },
  novation:        { label: "Novation",       icon: RefreshCcw,accent: "#10B981" },
  hybrid:          { label: "Hybrid",         icon: Layers,    accent: "#EA580C" },
};

const fmtMoney = (n) => new Intl.NumberFormat("en-US",
  { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
const fmtPct = (n) => `${Math.round((n || 0) * 100)}%`;

export default function OfferIntelTab({ property }) {
  const propertyId = property?.id;
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/v3/offer-intel/${propertyId}`);
      setMatrix(data);
    } catch { setMatrix(null); }
    finally { setLoading(false); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const generate = useCallback(async () => {
    if (!propertyId) return;
    setBusy(true);
    try {
      const { data } = await api.post("/v3/offer-intel/generate",
        { property_id: propertyId, force: true });
      setMatrix(data);
      toast.success(`Generated ${data.strategies?.length ?? 0} offer strategies.`);
    } catch { toast.error("Generation failed."); }
    finally { setBusy(false); }
  }, [propertyId]);

  if (loading) {
    return <div className="flex items-center justify-center py-16">
      <Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  }

  if (!matrix) {
    return (
      <div className="rounded-xl border-2 border-dashed border-slate-200 p-8 text-center"
           data-testid="offer-intel-tab-empty">
        <Sparkles className="w-8 h-8 text-amber-500 mx-auto" />
        <h3 className="mt-3 text-base font-bold text-slate-900">No offer intelligence yet</h3>
        <p className="mt-1 text-sm text-slate-500">Generate all 5 strategies for this property.</p>
        <button type="button" onClick={generate} disabled={busy}
                data-testid="offer-intel-tab-generate"
                className="mt-4 inline-flex items-center gap-1.5 rounded-lg text-white text-xs font-bold px-3 py-2 disabled:opacity-60"
                style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #EA580C)" }}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          Generate Offers
        </button>
      </div>
    );
  }

  const rec = matrix.strategies.find((s) => s.strategy === matrix.recommended);
  const RecIcon = STRATEGY_META[rec?.strategy]?.icon || Coins;
  const accent = STRATEGY_META[rec?.strategy]?.accent || "#F59E0B";

  return (
    <div className="space-y-4" data-testid="offer-intel-tab">
      {/* Recommended */}
      {rec && (
        <div className="rounded-xl border-2 border-amber-300/60 bg-gradient-to-br from-amber-50 to-white p-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 rounded-full text-[9px] font-black tracking-widest uppercase px-2 py-0.5 text-white"
                  style={{ backgroundImage: "linear-gradient(90deg, #F59E0B, #EA580C)" }}>
              <Award className="w-3 h-3" /> Recommended
            </span>
            <span className="text-[10px] font-black tracking-widest uppercase text-slate-400">
              {matrix.ai_source === "claude" ? "Claude" : "Deterministic"}
            </span>
          </div>
          <div className="mt-2 flex items-start gap-3 flex-wrap">
            <div className="grid place-items-center h-11 w-11 rounded-lg text-white shadow"
                 style={{ background: `linear-gradient(135deg, ${accent}, #EA580C)` }}>
              <RecIcon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Strategy</div>
              <div className="text-xl font-bold text-slate-900 leading-tight">{rec.label}</div>
              <p className="mt-1 text-[12px] text-slate-600 leading-snug">{matrix.recommendation_reason}</p>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[12px]">
            <MiniStat icon={DollarSign} label="Offer"          value={fmtMoney(rec.math.offer_amount)} />
            <MiniStat icon={DollarSign} label="Net to seller"  value={fmtMoney(rec.math.net_to_seller)} />
            <MiniStat icon={DollarSign} label="Est. profit"    value={fmtMoney(rec.math.expected_profit)} highlight />
            <MiniStat icon={Timer}      label="Close"          value={`${rec.math.close_days}d`} />
          </div>
          {matrix.ai_summary && (
            <div className="mt-3 rounded-lg bg-white border border-amber-200 p-3 text-[12.5px] leading-relaxed text-slate-700 whitespace-pre-line">
              {matrix.ai_summary}
            </div>
          )}
        </div>
      )}

      {/* Mini comparison */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-[10px] font-black tracking-widest uppercase text-emerald-600">All strategies</div>
        <div className="mt-3 overflow-hidden rounded-lg border border-slate-100">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Strategy</th>
                <th className="text-right px-3 py-2">Offer</th>
                <th className="text-right px-3 py-2">Profit</th>
                <th className="text-right px-3 py-2">ROI</th>
                <th className="text-right px-3 py-2">Close</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {matrix.strategies.map((s) => {
                const Icon = STRATEGY_META[s.strategy]?.icon || Coins;
                const isRec = s.strategy === matrix.recommended;
                return (
                  <tr key={s.strategy} className={isRec ? "bg-amber-50/40" : ""}>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-800">
                        <Icon className="w-3.5 h-3.5" style={{ color: STRATEGY_META[s.strategy]?.accent }} />
                        {s.label}
                        {isRec && <Award className="w-3 h-3 text-amber-500" />}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-[12px] text-slate-700">{fmtMoney(s.math.offer_amount)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-[12px] font-bold text-slate-900">{fmtMoney(s.math.expected_profit)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-[12px] text-slate-700">{fmtPct(s.math.expected_roi)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-[12px] text-slate-700">{s.math.close_days}d</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap pt-2 border-t border-slate-100">
        <button type="button" onClick={generate} disabled={busy}
                data-testid="offer-intel-tab-regenerate"
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 text-slate-700 text-xs font-semibold px-3 py-1.5 hover:bg-slate-50 disabled:opacity-60">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
          Regenerate
        </button>
        <Link to="/v3/intel/offer-intel"
              data-testid="offer-intel-tab-deeplink"
              className="inline-flex items-center gap-1.5 rounded-lg text-amber-700 text-xs font-bold px-3 py-1.5 hover:bg-amber-50">
          Open Offer Intelligence workspace <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, highlight }) {
  return (
    <div className={`rounded-lg px-2.5 py-1.5 border ${highlight ? "border-emerald-200 bg-emerald-50/60" : "border-slate-100 bg-slate-50"}`}>
      <div className="flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-widest text-slate-500">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`mt-0.5 font-bold tabular-nums ${highlight ? "text-emerald-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}
